import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { ArrowUp, Loader2, Play, Quote, X } from 'lucide-react'
import api from 'api/api'
import answerModel from 'components/answers/answerModel'
import StaticAnswerBody from 'components/answers/StaticAnswerBody'
import { openCenter } from 'components/filters/useStreamFilters'
import questModel from 'components/quests/questModel'
import UserIcon from 'components/users/UserIcon'
import Upvalue from 'components/votes/Upvalue'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import { cacheAnswers } from 'state/cache'
import useQuestChannel from 'state/channels/useQuestChannel'
import useStore from 'state/knovStore'
import {
    mergeRadioChatMessages,
    radioChatPlay,
    radioChatRows,
    reconcileRadioChatWindow,
} from 'lib/radioChat'
import type { DmChatRun } from 'lib/dmChat'
import type { Answer } from 'types/answers'
import type { Quest } from 'types/quests'
import styles from './radio-chat.module.scss'

function formatMessageTime(value: Date | string | number | null | undefined) {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

type ChatQuest = Pick<Quest, 'id' | 'space_id' | 'team_id' | 'public' | 'private'>

type ChatPage = {
    quest: ChatQuest
    messages: Answer[]
    hasMore: boolean
    // Opaque, from the server: pass back as before= for the page above.
    cursor: string | null
}

function radioChatUrl() {
    return (typeof gon !== 'undefined' ? gon?.api?.radioChat : null) || '/api/v1/radio/chat'
}

// One page of the station chat, oldest first: the latest, or the one above
// `before` (a page's cursor). The thread never rotates, so it is only ever
// read a window at a time.
async function fetchChatPage(before?: string | null): Promise<ChatPage | null> {
    const url = before ? `${radioChatUrl()}?before=${encodeURIComponent(before)}` : radioChatUrl()
    const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
    })
    if (!response.ok) return null
    const data = await response.json()
    if (!data?.quest?.id) return null
    const messages = Array.isArray(data.messages) ? data.messages : []
    // Upvalues land on the cached answer (optimistic, then confirmed) and rows
    // read it back, so the arrow fills right away. Only fresh reads go in:
    // re-caching older loaded copies would roll back a confirmed upvalue.
    // Song markers stay out: they come without their song embed.
    cacheAnswers(messages.filter((message: Answer) => !radioChatPlay(message)))
    return {
        quest: { ...data.quest, id: String(data.quest.id) },
        messages,
        hasMore: !!data.has_more,
        cursor: data.cursor ? String(data.cursor) : null,
    }
}

/**
 * Dock Chat tab: the station's one ongoing chat, in the dm_chat look (runs by
 * sender, one face and byline per run). Each song that airs shows up as a
 * "Now playing" marker linking to its own thread.
 */
export default function RadioChat({
    songTitle,
    nowPlayIndex,
}: {
    songTitle?: string | null
    nowPlayIndex?: number | null
}) {
    const [page, setPage] = useState<ChatPage | null>(null)
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

    useEffect(() => {
        let cancelled = false
        fetchChatPage()
            .catch(() => null)
            .then(first => {
                if (cancelled) return
                setPage(first)
                setStatus(first ? 'ready' : 'error')
            })
        return () => {
            cancelled = true
        }
    }, [])

    if (!page) {
        return (
            <div className={styles.state}>
                {status === 'loading' ? 'Tuning in…' : 'Couldn\u2019t load the chat.'}
            </div>
        )
    }
    return <RadioChatRoom initialPage={page} songTitle={songTitle} nowPlayIndex={nowPlayIndex} />
}

function RadioChatRoom({
    initialPage,
    songTitle,
    nowPlayIndex,
}: {
    initialPage: ChatPage
    songTitle?: string | null
    nowPlayIndex?: number | null
}) {
    const quest = initialPage.quest
    const questId = quest.id
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)

    const threadRef = useRef<HTMLDivElement>(null)
    const atBottomRef = useRef(true)
    // Scroll height before older messages were prepended, to keep the view still.
    const prependAnchorRef = useRef<number | null>(null)
    const [messages, setMessages] = useState<Answer[]>(initialPage.messages)
    const [hasMore, setHasMore] = useState(initialPage.hasMore)
    const [cursor, setCursor] = useState(initialPage.cursor)
    const [loadingEarlier, setLoadingEarlier] = useState(false)
    const [message, setMessage] = useState('')
    const [sending, setSending] = useState(false)
    // Sent messages show at once from here (dimmed until saved), apart from
    // the loaded history so they cannot hide a gap from refreshLatest; a read
    // that includes one takes its place.
    const [outbox, setOutbox] = useState<{ answer: Answer; saved: boolean }[]>([])
    // A play marker the next message quotes (sent as its embed).
    const [quoting, setQuoting] = useState<Answer | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    // A send that failed may still sit in the API outbox for replay; retrying
    // the same text resends that message (same id) so it cannot post twice.
    const failedPayloadRef = useRef<{
        text: string
        payload: Answer
        quotedId: Answer['id'] | null
    } | null>(null)

    const messagesRef = useRef(messages)
    messagesRef.current = messages
    const unsavedIds = useMemo(
        () => new Set(outbox.filter(sent => !sent.saved).map(sent => String(sent.answer.id))),
        [outbox],
    )
    const rows = useMemo(
        () =>
            radioChatRows<Answer>(
                mergeRadioChatMessages(
                    outbox.map(sent => sent.answer),
                    messages,
                ),
            ),
        [messages, outbox],
    )
    useEffect(() => {
        const loaded = new Set(messages.map(m => String(m.id)))
        setOutbox(current => {
            const left = current.filter(sent => !loaded.has(String(sent.answer.id)))
            return left.length === current.length ? current : left
        })
    }, [messages])

    // Live: the channel only signals; the dock rereads the latest page and
    // reconciles it, never the whole history. A signal that lands while a read
    // is in flight (that read may predate it) earns one more read after it.
    const refreshingRef = useRef<Promise<void> | null>(null)
    const refreshAgainRef = useRef(false)
    const refreshLatest = useCallback((): Promise<void> => {
        if (refreshingRef.current) {
            refreshAgainRef.current = true
            return refreshingRef.current
        }
        refreshAgainRef.current = false
        refreshingRef.current = fetchChatPage()
            .then(latest => {
                if (!latest) return
                // More than a page arrived since the last read (a long
                // disconnect): the loaded history no longer joins the latest
                // page, so start over from it rather than leave a hole that
                // "Load earlier" could not reach.
                const shown = new Set(messagesRef.current.map(m => String(m.id)))
                const joins = latest.messages.some(m => shown.has(String(m.id)))
                if (latest.hasMore && !joins) {
                    setMessages(latest.messages)
                    setHasMore(true)
                    setCursor(latest.cursor)
                    return
                }
                setMessages(current =>
                    reconcileRadioChatWindow(current, latest.messages, !latest.hasMore),
                )
            })
            .catch(() => {})
            .finally(() => {
                refreshingRef.current = null
                if (refreshAgainRef.current) void refreshLatest()
            })
        return refreshingRef.current
    }, [])
    // onConnected: the first subscription and every reconnect read the latest
    // page, covering whatever was posted while no signal could arrive.
    useQuestChannel(questId, {
        deferQuestUpdates: true,
        onDeferredQuestUpdate: refreshLatest,
        onConnected: refreshLatest,
    })

    const loadEarlier = useCallback(async () => {
        const before = cursor
        if (!before || loadingEarlier) return
        setLoadingEarlier(true)
        try {
            const older = await fetchChatPage(before)
            if (!older) return
            prependAnchorRef.current = threadRef.current?.scrollHeight ?? null
            setMessages(current => mergeRadioChatMessages(older.messages, current))
            setHasMore(older.hasMore)
            setCursor(older.cursor)
        } catch {
            // Leave the button for another try.
        } finally {
            setLoadingEarlier(false)
        }
    }, [cursor, loadingEarlier])

    // Newest at the bottom, like a chat; stay pinned there unless the reader
    // scrolled up. Prepending older messages keeps the view where it was.
    useLayoutEffect(() => {
        const thread = threadRef.current
        if (!thread) return
        if (prependAnchorRef.current != null) {
            thread.scrollTop += thread.scrollHeight - prependAnchorRef.current
            prependAnchorRef.current = null
            return
        }
        if (atBottomRef.current) thread.scrollTop = thread.scrollHeight
    }, [rows.length])

    const handleScroll = useCallback(() => {
        const thread = threadRef.current
        if (!thread) return
        atBottomRef.current = thread.scrollHeight - thread.scrollTop - thread.clientHeight < 24
    }, [])

    const submitMessage = useCallback(async () => {
        const text = message.trim()
        if (!text || sending) return
        if (!gon?.currentUser) {
            openAuthModal('signup', 'radio_chat')
            return
        }

        const retry = failedPayloadRef.current
        const quoted = quoting
        let payload: Answer
        if (retry && retry.text === text && retry.quotedId === (quoted?.id ?? null)) {
            payload = retry.payload
        } else {
            const deltaJson = { ops: [{ insert: text }, { insert: '\n' }] }
            const draft = answerModel.newAnswer({
                quest_id: quest.id,
                space_id: quest.space_id,
                content: text,
                delta_json: deltaJson,
            }) as Answer
            const childQuest = questModel.newQuest({
                ...questModel.getPerms(quest as Quest),
                parent_id: draft.id,
                parent: draft,
                is_draft: false,
                isLocalChildQuest: true,
                space_id: quest.space_id,
                sorted_answers: [],
                sorted_answer_ids: [],
            })
            payload = {
                ...draft,
                is_draft: false,
                content: text,
                delta_json: deltaJson,
                child_quests: [{ id: childQuest.id } as Quest],
                // action_tags too, so the quote shows before the server's copy lands.
                ...(quoted
                    ? { embeds: [{ id: String(quoted.id), action_tags: quoted.action_tags }] }
                    : {}),
            }
        }
        setMessage('')
        setQuoting(null)
        setSending(true)
        atBottomRef.current = true
        const shown = {
            ...payload,
            user_id: String(gon.currentUser.id),
            user: gon.currentUser,
            created_at: new Date(),
        } as Answer
        setOutbox(current => [...current, { answer: shown, saved: false }])
        const isThis = (sent: { answer: Answer }) => String(sent.answer.id) === String(payload.id)
        try {
            const created = (await api.createAnswer(payload)) as Answer | null
            failedPayloadRef.current = null
            if (created?.id) cacheAnswers([created])
            setOutbox(current =>
                current.map(sent =>
                    isThis(sent)
                        ? { answer: created?.id ? created : sent.answer, saved: true }
                        : sent,
                ),
            )
            // Others' messages since the last read come in the background.
            void refreshLatest()
        } catch (error) {
            setOutbox(current => current.filter(sent => !isThis(sent)))
            failedPayloadRef.current = { text, payload, quotedId: quoted?.id ?? null }
            setMessage(current => current || text)
            setQuoting(current => current || quoted)
            useStore
                .getState()
                .showGlobalNotice(
                    api.getReadableApiErrorMessage(error, 'Unable to post this message.'),
                    { variant: 'error', durationMs: 6500 },
                )
        } finally {
            setSending(false)
        }
    }, [message, openAuthModal, quest, quoting, refreshLatest, sending])

    return (
        <div className={styles.chat}>
            <div ref={threadRef} className={styles.thread} onScroll={handleScroll}>
                <div className={styles.threadContent}>
                    {hasMore && (
                        <button
                            type="button"
                            className={styles.earlier}
                            onClick={() => void loadEarlier()}
                            disabled={loadingEarlier}
                        >
                            {loadingEarlier ? 'Loading…' : 'Load earlier messages'}
                        </button>
                    )}
                    {rows.length === 0 ? (
                        <div className={styles.state}>
                            {songTitle ? `Say something about ${songTitle}.` : 'Say hi.'}
                        </div>
                    ) : (
                        rows.map(row => {
                            const { message: answer, day } = row
                            if (row.kind === 'play') {
                                const { play } = row
                                return (
                                    <React.Fragment key={answer.id}>
                                        {day && <div className={styles.day}>{day}</div>}
                                        <div className={styles.playRow}>
                                            <button
                                                type="button"
                                                className={styles.play}
                                                onClick={() =>
                                                    play.play_quest_id &&
                                                    openCenter({ questId: play.play_quest_id })
                                                }
                                                title="Open this song's thread"
                                            >
                                                <Play aria-hidden="true" />
                                                <span className={styles.playLabel}>
                                                    {nowPlayIndex != null &&
                                                    play.play_index === nowPlayIndex
                                                        ? 'Now playing'
                                                        : 'Played'}
                                                </span>
                                                <span className={styles.playTitle}>
                                                    {play.title || 'a song'}
                                                </span>
                                                <time>{formatMessageTime(answer.created_at)}</time>
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.playQuote}
                                                onClick={() => {
                                                    setQuoting(answer)
                                                    inputRef.current?.focus()
                                                }}
                                                aria-label={`Quote ${play.title || 'this song'}`}
                                                title="Quote this song"
                                            >
                                                <Quote aria-hidden="true" />
                                            </button>
                                        </div>
                                    </React.Fragment>
                                )
                            }
                            return (
                                <React.Fragment key={answer.id}>
                                    {day && <div className={styles.day}>{day}</div>}
                                    <RadioChatMessage
                                        answer={answer}
                                        run={row.run}
                                        pending={unsavedIds.has(String(answer.id))}
                                    />
                                </React.Fragment>
                            )
                        })
                    )}
                </div>
            </div>
            {quoting && (
                <div className={styles.quoting}>
                    <Quote aria-hidden="true" />
                    <span className={styles.quotingTitle}>
                        {radioChatPlay(quoting)?.title || 'a song'}
                    </span>
                    <button
                        type="button"
                        onClick={() => setQuoting(null)}
                        aria-label="Stop quoting"
                    >
                        <X aria-hidden="true" />
                    </button>
                </div>
            )}
            <form
                className={styles.composer}
                onSubmit={event => {
                    event.preventDefault()
                    void submitMessage()
                }}
            >
                <input
                    ref={inputRef}
                    className={styles.input}
                    value={message}
                    onChange={event => setMessage(event.target.value)}
                    placeholder="Message Treechat Music"
                    aria-label="Message the radio chat"
                    // Read-only, not disabled: focus (and the phone keyboard)
                    // stays, and a failed send gets its text back.
                    readOnly={sending}
                />
                <button
                    type="submit"
                    className={styles.send}
                    disabled={sending || !message.trim()}
                    aria-label="Send message"
                >
                    {sending ? (
                        <Loader2 className={styles.spinner} size={16} aria-hidden />
                    ) : (
                        <ArrowUp size={16} strokeWidth={2.4} aria-hidden />
                    )}
                </button>
            </form>
        </div>
    )
}

function RadioChatMessage({
    answer,
    run,
    pending = false,
}: {
    answer: Answer
    run: DmChatRun
    pending?: boolean
}) {
    const cached = useCachedAnswer(String(answer.id)) as Answer | null
    // The arrow pulses here when the message's value changes.
    const upvalueRef = useRef<HTMLDivElement>(null)
    const live = cached || answer
    const user = live.user || { id: live.user_id, name: 'Unknown' }
    // A quoted song (the play marker it embeds) sits above the text.
    const quotedPlay = live.embeds?.map(embed => radioChatPlay(embed)).find(play => !!play) || null
    return (
        <article
            className={cn(
                styles.message,
                run === 'cont' && styles.messageCont,
                pending && styles.messagePending,
            )}
        >
            {run === 'head' ? (
                <UserIcon user={user} size="small" contextStyles={styles.avatar} />
            ) : (
                <span className={styles.avatarGap} aria-hidden />
            )}
            <div className={styles.main}>
                {run === 'head' && (
                    <div className={styles.byline}>
                        <strong>{user.name || 'Unknown'}</strong>
                        <time>{formatMessageTime(live.created_at)}</time>
                    </div>
                )}
                {quotedPlay && (
                    <button
                        type="button"
                        className={styles.quoted}
                        onClick={() =>
                            quotedPlay.play_quest_id &&
                            openCenter({ questId: quotedPlay.play_quest_id })
                        }
                        title="Open this song's thread"
                    >
                        <Play aria-hidden="true" />
                        <span>{quotedPlay.title || 'a song'}</span>
                    </button>
                )}
                <div className={styles.body}>
                    <StaticAnswerBody
                        value={
                            live.delta_json || {
                                ops: [{ insert: live.content || '' }, { insert: '\n' }],
                            }
                        }
                        answer={live}
                        type="answer"
                    />
                </div>
            </div>
            {/* Nothing to upvalue until the server has it. */}
            {!pending && (
                <div ref={upvalueRef} className={styles.upvalue}>
                    <Upvalue
                        answer={live}
                        postLockRef={upvalueRef}
                        thin
                        quickOnly
                        source="radio_chat"
                    />
                </div>
            )}
        </article>
    )
}
